// Core search functionality
import { search } from './util/searchClient.js';
import { opts } from './opts.js';
const { facets } = opts;

// Widgets and configurations
import { widgets } from './widgets/index.js';
import { configs } from './widgets/config.js';
import { facetWidget } from './widgets/facet.js';
import { configure, index } from '../../../dist/vendor/instantsearch.js/es/widgets/index.js';

export class AlgoliaSearch {
	constructor(node, options = {}) {
		if (!node) {
			console.error('AlgoliaSearch: Node is required');
			return;
		}

		this.opts = {
			...opts.base,
			...options,
		};
		this.node = node;
		this.search = search;
		this.initialized = false;

		try {
			this.setup();
			this.initialized = true;
			this.start();
		} catch (error) {
			console.error('AlgoliaSearch: Initialization failed:', error);
			this.handleError(error);
		}
	}

	setup() {
		const { searchWrap } = this.opts.selectors;

		if (!searchWrap) {
			throw new Error('Search wrap selector is required');
		}

		this.wrap = this.node.querySelector(searchWrap);
		if (!this.wrap) {
			throw new Error(`Search wrap element not found: ${searchWrap}`);
		}

		this.attributes = [...this.node.attributes];
		this.setupErrorReporting();
		this.gatherRequirements();
		this.prepareWidgets();
	}

	start() {
		if (!this.initialized) {
			console.warn('AlgoliaSearch: Cannot start - not properly initialized');
			return;
		}
		this.search.start();
	}

	handleError(error) {
		// Add error state to DOM
		this.node.classList.add('algolia-error');
		const errorMsg = document.createElement('div');
		errorMsg.className = 'algolia-error-message';
		errorMsg.textContent = 'Search initialization failed. Please try again later.';
		this.node.appendChild(errorMsg);
	}

	setupErrorReporting() {
		this.search.on('render', () => {
			if (this.node.classList.contains('algolia-error')) {
				this.node.classList.remove('algolia-error');
				const errorMsg = this.node.querySelector('.algolia-error-message');
				errorMsg?.remove();
			}
		});

		this.search.on('error', err => {
			console.error('AlgoliaSearch: Runtime error:', err);
			this.handleError(err);
		});
	}

	gatherRequirements() {
		// Cache lowercase mappings
		this.widgetMap = new Map(
			Object.entries(widgets).map(([key, value]) => [key.toLowerCase(), { key, value }])
		);
		this.configMap = new Map(
			Object.entries(configs).map(([key, value]) => [key.toLowerCase(), { key, value }])
		);

		// Get attribute names we care about
		const attributeNames = new Set([...this.widgetMap.keys(), ...this.configMap.keys()]);

		// Filter attributes to only those we care about
		const relevantAttrs = this.attributes
			.filter(({ name }) => attributeNames.has(name.toLowerCase()));

		// Gather required widgets
		this.requires = relevantAttrs
			.map(attr => this.widgetMap.get(attr.name.toLowerCase())?.key)
			.filter(Boolean);
	}

	prepareWidgets() {
		if (!this.requires || !this.search) {
			throw new Error('Search not properly initialized');
		}

		const widgetConfigs = [
			// Core configuration
			configure(opts.configure),

			// Required widgets with their configs
			...this.requires
				.map(name => {
					const config = configs[name];
					if (!config) return null;

					const container = this.node.querySelector(config.container);
					if (!container) {
						console.warn(`Container not found for widget: ${name}`);
						return null;
					}

					return widgets[name]({
						...config,
						container
					});
				})
				.filter(Boolean),

			// Facet widgets
			...this.prepareFacetWidgets()
		];

		this.search.addWidgets(widgetConfigs);
	}

	prepareFacetWidgets() {
		return facets
			.map(facet => {
				const widget = facetWidget(facet);
				if (!widget) return null;

				const { params } = widget;
				const container = this.node.querySelector(params.container);

				if (!container) {
					console.warn(`Container not found for facet: ${params.label || params.attribute}`);
					return null;
				}

				return widget.widget({
					...params,
					container
				});
			})
			.filter(Boolean);
	}

	widgetName({ nodeName }) {
		return Object.keys(widgets).filter(w => nodeName === w.toLowerCase());
	}

	errorReporting() {
		this.search.on('render', () => console.log(this.search.status));
		this.search.on('error', err => console.log(this.search.status, err));
	}
}

// Initialize search when DOM is ready
const initAlgoliaSearch = () => {
	const blocks = document.querySelectorAll('.algolia__block');
	blocks.forEach(block => new AlgoliaSearch(block));
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAlgoliaSearch);
} else {
	initAlgoliaSearch();
}
